import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnUnderline, BtnNumberedList, BtnBulletList, BtnLink, BtnUndo, BtnRedo, Separator, BtnClearFormatting, HtmlButton, BtnStyles, createDropdown } from 'react-simple-wysiwyg';
import MediaSelector from '../../admin/MediaSelector';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { API_URLS, API_CONFIG } from '../../../config/api';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, Calendar, User, Tag, Heart, Smartphone, X, Globe, Image as ImageIcon } from 'lucide-react';

// react-simple-wysiwyg CSS stilleri
const editorStyles = `
  .rsw-editor {
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    min-height: 200px;
    padding: 12px;
  }
  
  .rsw-toolbar {
    border-bottom: 1px solid #d1d5db;
    padding: 8px;
    background: #f9fafb;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
  }
  
  .rsw-btn {
    padding: 6px 8px;
    margin: 0 2px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .rsw-btn:hover {
    background: #f3f4f6;
  }
  
  .rsw-btn.active {
    background: #3b82f6;
    color: white;
  }
  
  .rsw-dd {
    position: relative;
    display: inline-block;
  }
  
  .rsw-dd-content {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    min-width: 120px;
  }
  
  .rsw-dd-content button {
    display: block;
    width: 100%;
    padding: 8px 12px;
    text-align: left;
    border: none;
    background: none;
    cursor: pointer;
  }
  
  .rsw-dd-content button:hover {
    background: #f3f4f6;
  }
  
  .rsw-separator {
    width: 1px;
    height: 20px;
    background: #d1d5db;
    margin: 0 4px;
  }
`;

// Özelleştirilmiş Styles dropdown (P, H1-H6, PRE)
const CustomStyles = createDropdown('Styles', [
  ['Paragraf', 'formatBlock', 'P'],
  ['Başlık 1', 'formatBlock', 'H1'],
  ['Başlık 2', 'formatBlock', 'H2'],
  ['Başlık 3', 'formatBlock', 'H3'],
  ['Başlık 4', 'formatBlock', 'H4'],
  ['Başlık 5', 'formatBlock', 'H5'],
  ['Başlık 6', 'formatBlock', 'H6'],
  ['Kod', 'formatBlock', 'PRE'],
]);

// Resim Seçici Modal Component
const ImageSelectorModal = ({ onSelect, onClose }) => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const BASE_URL = API_CONFIG.BASE_URL;

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URLS.ADMIN.MEDIA, { headers: { Accept: 'application/json' } });
      const text = await response.text();
      if (!response.ok) {
        setMediaFiles([]);
        return;
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        setMediaFiles([]);
        return;
      }
      const files = Array.isArray(data) ? data : (data?.data || []);
      // Sadece resimleri filtrele
      setMediaFiles(files.filter(file => (file?.type || '').startsWith('image/')));
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = mediaFiles.filter(file => {
    return (file?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelectFile = (file) => {
    const fullUrl = (file.url.startsWith('http://') || file.url.startsWith('https://'))
      ? file.url
      : `${BASE_URL}${file.url}`;
    onSelect(fullUrl);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10 rounded-t-lg">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Resim Seç</h2>
            <p className="text-sm text-gray-600 mt-1">İçeriğe eklenecek resmi seçin</p>
          </div>
          <Button variant="outline" onClick={onClose}>
            Kapat
          </Button>
        </div>
        <div className="p-4 sm:p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Resimlerde ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-500">Yükleniyor...</div>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-500">
                  {searchTerm ? 'Arama kriterlerinize uygun resim bulunamadı' : 'Henüz yüklenmiş resim yok'}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {filteredFiles.map((file, index) => {
                  const fileUrl = (file.url.startsWith('http://') || file.url.startsWith('https://'))
                    ? file.url
                    : `${BASE_URL}${file.url}`;
                  return (
                    <div
                      key={file.id || file.url || index}
                      onClick={() => handleSelectFile(file)}
                      className="relative group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-purple-500 transition-all"
                    >
                      <div className="aspect-square bg-gray-100 flex items-center justify-center">
                        <img
                          src={fileUrl}
                          alt={file.name || 'Resim'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center text-gray-400"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                          }}
                        />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                        <Button
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          Seç
                        </Button>
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate" title={file.name || 'Resim'}>
                          {file.name || 'Resim'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deviceCounts, setDeviceCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    author_role: '',
    author_bio: '',
    read_time: '5 dakika',
    level: 'Genel',
    image: '',
    image_focal_x: 50,
    image_focal_y: 50,
    tags: '',
    is_published: false
  });
  const [showEditorHelp, setShowEditorHelp] = useState(false);
  const [showImageSelector, setShowImageSelector] = useState(false);

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    const BASE_URL = API_CONFIG.BASE_URL;
    try {
      const u = String(url);
      if (u.startsWith('http://') || u.startsWith('https://')) {
        // Rewrite admin getScreenshot to direct uploads
        if (u.includes('admin_api.php') && u.includes('getScreenshot') && u.includes('file=')) {
          const match = u.match(/file=([^&]+)/);
          if (match && match[1]) return `${BASE_URL}/uploads/${decodeURIComponent(match[1])}`;
        }
        return u;
      }
      if (u.startsWith('/backend/uploads/')) return `${BASE_URL}${u.replace('/backend', '')}`;
      if (u.startsWith('/uploads/')) return `${BASE_URL}${u}`;
      return `${BASE_URL}/uploads/${u.replace(/^\//,'')}`;
    } catch { return url; }
  };

  // Generate URL-friendly slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    
    // Turkish character mapping
    const charMap = {
      'ç': 'c', 'Ç': 'c',
      'ğ': 'g', 'Ğ': 'g',
      'ı': 'i', 'İ': 'i',
      'ö': 'o', 'Ö': 'o',
      'ş': 's', 'Ş': 's',
      'ü': 'u', 'Ü': 'u'
    };
    
    let slug = title.toLowerCase();
    
    // Replace Turkish characters
    Object.keys(charMap).forEach(key => {
      slug = slug.replace(new RegExp(key, 'g'), charMap[key]);
    });
    
    // Remove special characters and replace spaces with hyphens
    slug = slug
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
    
    return slug;
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { adminApiFetch } = await import('../../../utils/adminApi');
      const response = await adminApiFetch(API_URLS.ADMIN.ADMIN_API + '?action=getBlogCategories');
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setCategories(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      const { adminApiFetch } = await import('../../../utils/adminApi');
      const response = await adminApiFetch(API_URLS.ADMIN.BLOGS);
      if (response.ok) {
        const data = await response.json();
        const list = data.data || [];
        setBlogs(list);
        try {
          const results = await Promise.all(
            (list || []).map(b => fetch(`${API_URLS.BASE_URL}/backend/api.php?action=getBlogDeviceCount&id=${encodeURIComponent(b.id)}`)
              .then(r => r.ok ? r.json() : { unique_devices: 0 })
              .catch(() => ({ unique_devices: 0 }))
            )
          );
          const map = {};
          list.forEach((b, idx) => { map[b.id] = results[idx]?.unique_devices ?? 0; });
          setDeviceCounts(map);
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingBlog 
        ? `${API_URLS.ADMIN.UPDATE_BLOG}`
        : API_URLS.ADMIN.CREATE_BLOG;
      
      const method = editingBlog ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(editingBlog ? { id: editingBlog.id } : {}),
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
        }),
      });

      if (response.ok) {
        const raw = await response.text();
        let result;
        try {
          result = JSON.parse(raw);
        } catch (e) {
          console.error('Non-JSON response while saving blog:', raw);
          alert('Sunucu beklenmeyen bir yanıt döndürdü.\n\n' + raw.substring(0, 500));
          return;
        }
        if (result.success || result.status === 'success') {
          setShowModal(false);
          setEditingBlog(null);
          setFormData({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            category: '',
            author: '',
            author_role: '',
            author_bio: '',
            read_time: '5 dakika',
            level: 'Genel',
            image: '',
            image_focal_x: 50,
            image_focal_y: 50,
            tags: '',
            is_published: false
          });
          fetchBlogs();
          alert(editingBlog ? 'Blog yazısı güncellendi!' : 'Blog yazısı oluşturuldu!');
        } else {
          alert((result.message || 'İşlem başarısız'));
        }
      } else {
        const raw = await response.text().catch(() => '');
        alert('Hata: Sunucu hatası' + (raw ? ('\n\n' + raw.substring(0, 500)) : ''));
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      slug: blog.slug || '',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      category: blog.category || '',
      author: blog.author || '',
      author_role: blog.author_role || '',
      author_bio: blog.author_bio || '',
      read_time: blog.read_time || '5 dakika',
      level: blog.level || 'Genel',
      image: normalizeImageUrl(blog.image || ''),
      image_focal_x: typeof blog.image_focal_x === 'number' ? blog.image_focal_x : 50,
      image_focal_y: typeof blog.image_focal_y === 'number' ? blog.image_focal_y : 50,
      tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : (blog.tags || ''),
      is_published: blog.is_published || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URLS.ADMIN.DELETE_BLOG}&blog_id=${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success || result.status === 'success') {
          fetchBlogs();
          alert('Blog yazısı silindi!');
        } else {
          alert((result.message || 'İşlem başarısız'));
        }
      } else {
        alert('Hata: Sunucu hatası');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.TOGGLE_BLOG_PUBLISH}&blog_id=${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_published: !currentStatus }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success || result.status === 'success') {
          fetchBlogs();
          alert(`Blog yazısı ${!currentStatus ? 'yayınlandı' : 'yayından kaldırıldı'}!`);
        } else {
          alert((result.message || 'İşlem başarısız'));
        }
      } else {
        alert('Hata: Sunucu hatası');
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Hata: ' + error.message);
    }
  };

  const getCategoryName = (categorySlug) => {
    const category = categories.find(cat => cat.slug === categorySlug);
    return category ? category.name : categorySlug;
  };

  const filteredBlogs = blogs.filter(blog =>
    blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && blogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6 p-2 sm:p-4 md:p-6 overflow-x-hidden max-w-full">
      <style>{editorStyles}</style>
      <div className="flex flex-col gap-2 sm:gap-4">
        <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold break-words">Blog Yazıları</h1>
        <Button onClick={() => setShowModal(true)} className="flex items-center justify-center gap-2 w-full sm:w-auto max-w-full">
          <Plus className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">Yeni Blog Yazısı</span>
        </Button>
      </div>

      <div className="flex gap-2 sm:gap-4 w-full max-w-full">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3 sm:h-4 sm:w-4" />
          <Input
            placeholder="Blog yazılarında ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 sm:pl-10 text-xs sm:text-sm md:text-base w-full max-w-full"
          />
        </div>
      </div>

      <div className="grid gap-2 sm:gap-3 md:gap-4 w-full max-w-full">
        {filteredBlogs.map((blog) => (
          <Card key={blog.id} className="w-full max-w-full overflow-hidden">
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 w-full max-w-full">
                <div className="flex-1 w-full min-w-0">
                  <CardTitle className="text-sm sm:text-base md:text-lg lg:text-xl break-words pr-2">{blog.title}</CardTitle>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 mt-1.5 sm:mt-2 text-[10px] sm:text-xs md:text-sm text-gray-600">
                    <div className="flex items-center gap-0.5 sm:gap-1 min-w-0">
                      <User className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0" />
                      <span className="truncate max-w-[80px] sm:max-w-none">{blog.author || '—'}</span>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                      <Calendar className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0" />
                      <span className="whitespace-nowrap">{new Date(blog.published_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 min-w-0">
                      <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0" />
                      <span className="truncate max-w-[60px] sm:max-w-none">{getCategoryName(blog.category)}</span>
                    </div>
                    {typeof blog.likes !== 'undefined' && (
                      <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                        <Heart className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 text-red-500 flex-shrink-0" />
                        <span>{blog.likes}</span>
                      </div>
                    )}
                    {typeof blog.views !== 'undefined' && (
                      <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                        <Eye className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0" />
                        <span>{Number(blog.views || 0).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                      <Smartphone className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0" />
                      <span>{typeof blog.unique_devices === 'number' ? blog.unique_devices : (deviceCounts[blog.id] ?? 0)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
                  <Badge variant={blog.is_published ? "default" : "secondary"} className="w-fit text-[10px] sm:text-xs">
                    {blog.is_published ? "Yayında" : "Taslak"}
                  </Badge>
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTogglePublish(blog.id, blog.is_published)}
                      className="flex-shrink-0 h-7 sm:h-8 w-7 sm:w-8 p-0"
                    >
                      {blog.is_published ? <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" /> : <Eye className="h-3 w-3 sm:h-4 sm:w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(blog)}
                      className="flex-shrink-0 h-7 sm:h-8 w-7 sm:w-8 p-0"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(blog.id)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0 h-7 sm:h-8 w-7 sm:w-8 p-0"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
              <p className="text-[11px] sm:text-xs md:text-sm text-gray-600 line-clamp-3 break-words">
                {blog.content?.substring(0, 200)}...
              </p>
              {blog.tags && blog.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                  {blog.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-[10px] sm:text-xs break-words">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 w-full max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-start gap-3 mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold break-words flex-1">
                {editingBlog ? 'Blog Yazısını Düzenle' : 'Yeni Blog Yazısı'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingBlog(null);
                  setFormData({
                    title: '',
                    slug: '',
                    excerpt: '',
                    content: '',
                    category: '',
                    author: '',
                    author_role: '',
                    author_bio: '',
                    read_time: '5 dakika',
                    level: 'Genel',
                    image: '',
                    image_focal_x: 50,
                    image_focal_y: 50,
                    tags: '',
                    is_published: false
                  });
                  setShowImageSelector(false);
                }}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Kapat"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Başlık</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      const newSlug = generateSlug(newTitle);
                      setFormData({ ...formData, title: newTitle, slug: newSlug });
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Slug 
                    <span className="text-xs text-gray-500 ml-2">(Otomatik oluşturuldu, düzenleyebilirsiniz)</span>
                  </label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="url-friendly-slug"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1">Kategori</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.filter(cat => cat.is_active).map((category) => (
                      <option key={category.id} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1">Yazar</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="text-sm sm:text-base"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1">Yazar Rolü</label>
                  <Input
                    value={formData.author_role}
                    onChange={(e) => setFormData({ ...formData, author_role: e.target.value })}
                    placeholder="Örn: Yazılım Geliştirici"
                    className="text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1">Okuma Süresi</label>
                  <Input
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    placeholder="5 dakika"
                    className="text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Seviye</label>
                  <Input
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    placeholder="Genel"
                  />
                </div>
                <div>
                  <MediaSelector
                    value={formData.image}
                    onChange={(url) => setFormData({ ...formData, image: url })}
                    label="Görsel"
                    placeholder="Medya kütüphanesinden seçin..."
                  />
                  {formData.image && (
                    <div className="mt-3">
                      <label className="block text-xs sm:text-sm font-medium mb-1">Odak Noktası</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">X (%)</label>
                          <input type="range" min="0" max="100" value={formData.image_focal_x}
                            onChange={(e) => setFormData({ ...formData, image_focal_x: Number(e.target.value) })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Y (%)</label>
                          <input type="range" min="0" max="100" value={formData.image_focal_y}
                            onChange={(e) => setFormData({ ...formData, image_focal_y: Number(e.target.value) })}
                            className="w-full"
                          />
                        </div>
                        <div className="text-xs text-gray-600 break-words">{formData.image_focal_x}% , {formData.image_focal_y}%</div>
                      </div>
                      <div className="mt-2 w-full h-32 sm:h-40 rounded border overflow-hidden bg-gray-50">
                        <img
                          src={formData.image}
                          alt="Kapak Önizleme"
                          className="w-full h-full object-cover"
                          style={{ objectPosition: `${formData.image_focal_x}% ${formData.image_focal_y}%` }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 break-words">Bu ayar görüntüleme sırasında CSS ile uygulanır (gerçek kırpma değildir).</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Yazar Bio</label>
                <Input
                  value={formData.author_bio}
                  onChange={(e) => setFormData({ ...formData, author_bio: e.target.value })}
                  placeholder="Yazar hakkında kısa bilgi"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Özet</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full h-20 p-3 text-sm sm:text-base border border-gray-300 rounded-md"
                  placeholder="Blog yazısının kısa özeti"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1">Etiketler (virgülle ayırın)</label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="etiket1, etiket2, etiket3"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs sm:text-sm font-medium">İçerik (Editör)</label>
                </div>
                <EditorProvider>
                  <Toolbar className="overflow-x-auto" style={{ display: 'flex' }}>
                    <BtnUndo />
                    <BtnRedo />
                    <Separator />
                    <CustomStyles />
                    <Separator />
                    <BtnBold />
                    <BtnItalic />
                    <BtnUnderline />
                    <Separator />
                    <BtnNumberedList />
                    <BtnBulletList />
                    <Separator />
                    <BtnLink />
                    <Separator />
                    <button
                      type="button"
                      onClick={() => setShowImageSelector(true)}
                      className="rsw-btn"
                      title="Resim Ekle"
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <Separator />
                    <BtnClearFormatting />
                    <Separator />
                    <HtmlButton />
                  </Toolbar>
                  <Editor
                    value={formData.content || ''}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    containerProps={{ 
                      className: 'rsw-editor',
                      style: { 
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }
                    }}
                  />
                </EditorProvider>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="flex-shrink-0"
                />
                <label htmlFor="is_published" className="text-xs sm:text-sm font-medium break-words">
                  Yayınla
                </label>
              </div>


              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBlog(null);
                    setFormData({
                      title: '',
                      slug: '',
                      excerpt: '',
                      content: '',
                      category: '',
                      author: '',
                      author_role: '',
                      author_bio: '',
                      read_time: '5 dakika',
                      level: 'Genel',
                      image: '',
                      image_focal_x: 50,
                      image_focal_y: 50,
                      tags: '',
                      is_published: false
                    });
                  }}
                >
                  İptal
                </Button>
                <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                  {loading ? 'Kaydediliyor...' : (editingBlog ? 'Güncelle' : 'Oluştur')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resim Seçici Modal */}
      {showImageSelector && (
        <ImageSelectorModal
          onSelect={(url) => {
            if (url) {
              // Resmi editör içine ekle
              const imgHTML = `<p><img src="${url}" alt="" style="max-width: 100%; height: auto;" /></p>`;
              const currentContent = formData.content || '';
              const newContent = currentContent + imgHTML;
              setFormData({ ...formData, content: newContent });
              setShowImageSelector(false);
            }
          }}
          onClose={() => setShowImageSelector(false)}
        />
      )}
    </div>
  );
};

export default AdminBlogs;
